<template>
  <Card class="loan-card">
    <vbLink :to="route" class="loan-image-link">
      <img v-if="loanImage" :alt="loan.entrepreneur.displayName" :src="loanImage">
    </vbLink>
    <CardSection :dark="true">
      <div class="grid-x">
        <div class="cell small-8">
          <vbLink :to="route" class="h3">
            {{ loan.entrepreneur.displayName }}
          </vbLink>
        </div>
        <div class="cell auto indicator-cell">
          <FundingProgressIndicator ref="progress"
                                    class="indicator"
                                    :funded="loan.fundingAmountFunded"
                                    :requested="loan.fundingAmount"
                                    :size="$device.isSmall ? 'small' : 'large'"
                                    :status="loan.status" />
        </div>
      </div>
    </CardSection>
    <CardSection :dark="true" :padding="false">
      <div class="meta-grid">
        <MetaItem :icon="icons.mapMarker">
          {{ location }}
        </MetaItem>
        <MetaItem :icon="icons.activityType">
          {{ loan.activity }}
        </MetaItem>
        <MetaItem :icon="icons.amountRequested">
          <span>{{ $t('requested') }}:</span>
          <strong>{{ loan.formattedFundingAmount }}</strong>
        </MetaItem>
      </div>
    </CardSection>
    <slot>
      <CardSection>
        <p>{{ loan.purpose }}</p>
        <vb-link v-if="!compact" :to="route">{{ $t('viewDetails') }} &gt;</vb-link>
      </CardSection>
      <CardSection>
        <hr v-if="!compact" class="margin-top-0">
        <slot name="description">
          <p v-html="description" />
        </slot>
        <QuickLend v-if="isQuickLendRenderable"
                   :loan-id="loan.id"
                   :remaining="loan.fundingAmountRemaining"
                   :entrepreneur-display-name="loan.entrepreneur.displayName" />
      </CardSection>
    </slot>
  </Card>
</template>

<script>
  import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons/faMapMarkerAlt'
  import get from 'lodash/get'
  import { facActivityType } from '~/assets/font-awesome-custom/fac-activity-type'
  import { facAmountRequested } from '~/assets/font-awesome-custom/fac-amount-requested'
  import { Card, CardSection } from '~/components/frontend/ui/card'
  import FundingProgressIndicator
    from '~/modules/loans/components/frontend/funding-progress-indicator'
  import Loan from '~/modules/loans/entities/frontend/loan'
  import LoanImage from '~/modules/loans/entities/frontend/loan-image'
  import LoanStatusMixin from '~/modules/loans/mixins/loan-status'
  import i18n from './locales'
  import MetaItem from './meta-item.vue'
  import QuickLend from './quick-lend.vue'

  export default {
    name: 'LoanCard',
    i18n,
    mixins: [
      LoanStatusMixin,
    ],
    components: {
      Card,
      CardSection,
      FundingProgressIndicator,
      MetaItem,
      QuickLend,
    },
    props: {
      compact: {
        default: false,
        type: Boolean,
      },
      loan: {
        required: true,
        type: Loan,
      },
      quickLend: {
        default: true,
        type: Boolean,
      },
    },
    data () {
      return {
        icons: {
          activityType: facActivityType,
          amountRequested: facAmountRequested,
          mapMarker: faMapMarkerAlt,
        },
      }
    },
    computed: {
      description () {
        const entrepreneur = this.loan.entrepreneur
        const name = entrepreneur.type === 'individual'
          ? this.loan.entrepreneur.firstName
          : this.loan.entrepreneur.groupName

        switch (this.status) {
          case 'defaulted':
            return this.$t('descriptions.defaulted')
          case 'finished':
          case 'devalued':
            return this.$t('descriptions.finished.' + entrepreneur.type, {
              name,
              amount: this.loan.formattedFundingAmount,
            })
          case 'funded':
            let suffixedName = name
            suffixedName += name.endsWith('s') ? `'` : `'s`
            return this.$t('descriptions.funded', {
              name: suffixedName,
              amount: this.loan.formattedFundingAmount,
            })
          case 'open':
            const suffix = entrepreneur.type === 'group' ? 'group' : entrepreneur.gender
            return this.$t('descriptions.default.' + suffix, {
              name,
              amount: this.loan.formattedFundingAmountRemaning,
            })
        }
      },
      isQuickLendRenderable () {
        return this.quickLend && this.status === 'open'
      },
      loanImage () {
        if (this.$get(this.loan, 'mainImage') instanceof LoanImage) {
          return this.$get(this.loan, 'mainImage.medium')
        }

        return this.$get(this.loan, 'images.0.medium', null)
      },
      location () {
        return get(this.loan, 'entrepreneur.mfi.country')
      },
      route () {
        return {
          name: 'loans:frontend:show',
          params: {
            loanId: this.loan.id,
          },
        }
      },
    },
    methods: {
      updateProgress () {
        this.$refs.progress.update()
      },
    },
  }
</script>

<style lang="scss" scoped>
  @import "~assets/scss/frontend/_colours";
  @import "~assets/scss/frontend/_settings";
  @import "~foundation-sites/scss/util/unit";
  @import "~assets/scss/frontend/foundation/_settings";

  .loan-card /deep/ {
    .card__inner {
      display: flex;
      flex-direction: column;
    }
  }

  .loan-image-link {
    display: block;
  }

  $dark-section-border: $global-border;

  .meta-grid {
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    & > * {
      border: {
        bottom: $dark-section-border;
        top: $dark-section-border;
      }
      display: block;
      flex: 1 1 0;
      padding: 1rem;
      width: auto;
      min-height: 0;
      min-width: 0;

      &:not(:last-child) {
        border-right: $dark-section-border;
      }
    }
  }

  .indicator-cell {
    position: relative;

    .indicator {
      height: rem-calc(80);
      position: absolute;
      right: 0;
      top: rem-calc(-40) - 1rem;
      width: rem-calc(80);
    }
  }
</style>
